import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IntegrationConnectionStatus,
  IntegrationProvider,
  startConnection,
} from '@core/api/integrations/integrationsApi';
import {
  disconnectConnectedIntegration,
  fetchConnectedIntegrationStatus,
  setConnectedIntegrationStatus,
} from '@app/actions/integrations/emails';
import { getOutlookConnection } from '@core/selectors/integrations';
import { getHomeViewRole } from '@app/selectors/home';
import {
  trackOutlookConnected,
  trackOutlookDisconnected,
} from './outlookAnalytics';

const PROVIDER: IntegrationProvider = 'microsoft';

export type OutlookConnectionStatus = IntegrationConnectionStatus | 'none';

const getErrorCode = (err: unknown): string | undefined => {
  const axiosErr = err as {
    response?: { data?: { errors?: Array<{ code?: string }> } };
  };
  return axiosErr?.response?.data?.errors?.[0]?.code;
};

const useOutlookConnect = () => {
  const dispatch = useDispatch<any>();
  const connection = useSelector(getOutlookConnection);
  const homeViewRole = useSelector(getHomeViewRole);
  const location = useSelector(
    (state: any) => state.routing?.locationBeforeTransitions
  );

  const [connectError, setConnectError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const lastProcessedSearchRef = useRef<string | null>(null);

  const refreshConnectionStatus = useCallback(
    (force = false) => {
      dispatch(fetchConnectedIntegrationStatus(PROVIDER, { force }));
    },
    [dispatch]
  );

  useEffect(() => {
    refreshConnectionStatus();
  }, [refreshConnectionStatus]);

  // Reset isConnecting when returning to the page (e.g. user clicks browser back from OAuth)
  useEffect(() => {
    const handleReturnToPage = () => {
      setIsConnecting(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsConnecting(false);
      }
    };

    window.addEventListener('pageshow', handleReturnToPage);
    window.addEventListener('focus', handleReturnToPage);
    window.addEventListener('popstate', handleReturnToPage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handleReturnToPage);
      window.removeEventListener('focus', handleReturnToPage);
      window.removeEventListener('popstate', handleReturnToPage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle OAuth callback query parameters (e.g. ?integration=microsoft&result=error&reason=access_denied)
  useEffect(() => {
    const currentSearch =
      location?.search ||
      (typeof window !== 'undefined'
        ? window.location.hash.split('?')[1] || window.location.search
        : '');

    if (currentSearch && currentSearch === lastProcessedSearchRef.current) {
      return;
    }

    const getParam = (key: string): string | null => {
      if (location?.query && location.query[key]) {
        return location.query[key];
      }
      if (location?.search) {
        const p = new URLSearchParams(location.search);
        if (p.get(key)) return p.get(key);
      }
      if (typeof window !== 'undefined') {
        const hashIdx = window.location.hash.indexOf('?');
        if (hashIdx !== -1) {
          const p = new URLSearchParams(
            window.location.hash.substring(hashIdx)
          );
          if (p.get(key)) return p.get(key);
        }
        if (window.location.search) {
          const p = new URLSearchParams(window.location.search);
          if (p.get(key)) return p.get(key);
        }
      }
      return null;
    };

    const integration = getParam('integration');
    const result = getParam('result');
    const reason = getParam('reason');

    if (integration === PROVIDER) {
      lastProcessedSearchRef.current = currentSearch;
      if (result === 'error') {
        setConnectError(reason || 'access_denied');
        setIsConnecting(false);
      } else if (result === 'success') {
        setConnectError(null);
        setIsConnecting(false);
        trackOutlookConnected(homeViewRole);
        refreshConnectionStatus(true);
      }
    }
  }, [location, refreshConnectionStatus, homeViewRole]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setConnectError(null);
    try {
      const { authorizeUrl } = await startConnection(PROVIDER);
      window.location.assign(authorizeUrl);
    } catch (err) {
      setConnectError(getErrorCode(err) || 'connect_failed');
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true);
    setConnectError(null);
    try {
      await dispatch(disconnectConnectedIntegration(PROVIDER));
      trackOutlookDisconnected(homeViewRole);
    } catch (err) {
      setConnectError(getErrorCode(err) || 'disconnect_failed');
    } finally {
      setIsDisconnecting(false);
    }
  }, [dispatch, homeViewRole]);

  const markConnected = useCallback(() => {
    dispatch(setConnectedIntegrationStatus('active'));
  }, [dispatch]);

  return {
    errorCode: connectError || connection.errorCode,
    isConnecting,
    isDisconnecting,
    status: connection.status,
    accountEmail: connection.accountEmail,
    isConnected: connection.isConnected,
    needsReauth: connection.needsReauth,
    isLoadingStatus: connection.isLoadingStatus,
    connect,
    disconnect,
    markConnected,
    refreshConnectionStatus,
    provider: PROVIDER,
  };
};

export default useOutlookConnect;
